// Create / edit a lead. A lead is a client row with an owner (whose deal it is,
// required) and the referral fields. Stage is NOT edited here — it moves through the
// advance control on the board, which enforces the progressive gates.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import { useLookups } from '../../context/LookupsContext';
import { clientsApi, authApi } from '../../api';
import he from '../../i18n/he';
import Modal from '../../components/Modal';
import { TextInput, Select, ColorInput, DateInput, Textarea } from '../../components/Form';
import DueOffsets from '../../components/DueOffsets';
import { useAlerts } from '../../context/AlertsContext';
import { ErrorText } from '../../components/Loader';
import ContactsEditor from '../../components/ContactsEditor';
import { initialContacts, contactsPayload, contactsValid } from '../../utils/contacts';

export default function LeadModal({ lead, onClose, onSaved }) {
  const lookups = useLookups();
  const { data } = useAsync(() => authApi.partners());
  const partners = data || [];
  const isNew = !lead.id;
  const alerts = useAlerts();

  const [form, setForm] = useState({
    name: lead.name || '',
    owner_partner_id: lead.owner_partner_id || '',
    lead_source: lead.lead_source || '',
    referred_by: lead.referred_by || '',
    color: lead.color || '',
    // WHEN TO GET BACK TO THEM. Set here when the lead is created — the moment you know
    // it, because "I'll call him next week" is the first thing decided about a new
    // prospect — and editable from this same form for ever after.
    follow_up_on: lead.follow_up_on || '',
    // The STANDING description of the lead: who they are, what they want. One field,
    // updated in place. It is NOT the contact history — that accumulates, one row per
    // conversation, and lives in the card's own log (LeadNotes). The column has existed
    // since migration 002 and this form is the first thing ever to write it.
    notes: lead.notes || '',
  });
  // The contact people (071), on create and on edit. `lead` is the full client here —
  // Leads.js reads it before opening the form — so the whole set is edited, not just the
  // main contact the board shows.
  const [contacts, setContacts] = useState(() => initialContacts(lead));
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const ownerOptions = [
    { value: '', label: '—' },
    ...partners.map((p) => ({ value: p.id, label: p.name })),
  ];
  const sourceOptions = [
    { value: '', label: he.leads.noSource },
    ...lookups.sourceOptionsFor(form.lead_source),
  ];

  const submit = async () => {
    try {
      const payload = {
        name: form.name,
        owner_partner_id: Number(form.owner_partner_id) || null,
        contacts: contactsPayload(contacts),
        lead_source: form.lead_source || null,
        referred_by: form.referred_by || null,
        // Both explicitly nullable: clearing the date means "no follow-up planned", which
        // is a complete answer and the state most clients are in for ever.
        follow_up_on: form.follow_up_on || null,
        notes: form.notes || null,
      };
      if (form.color) payload.color = form.color;
      // The saved lead goes back to the caller — a meeting's "make them a lead" step links
      // its meetings to it (077). Every other caller ignores it.
      const saved = isNew
        ? await clientsApi.create({ ...payload, stage: 'new' })
        : await clientsApi.update(lead.id, payload);
      // The follow-up date is what rings the bell, so saving one refreshes it rather
      // than waiting for the next full page load to notice.
      alerts?.reload();
      onSaved(saved);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Modal
      title={isNew ? he.leads.newLead : he.leads.editLead}
      onClose={onClose}
      onSave={submit}
      saveDisabled={!form.name || !form.owner_partner_id || !contactsValid(contacts)}
    >
      <TextInput label={he.clients.name} value={form.name} onChange={set('name')} />
      <Select label={he.leads.owner} value={form.owner_partner_id} onChange={set('owner_partner_id')} options={ownerOptions} />
      <Select label={he.leads.source} value={form.lead_source} onChange={set('lead_source')} options={sourceOptions} />
      <TextInput label={he.leads.referredBy} value={form.referred_by} onChange={set('referred_by')} placeholder={he.leads.referredByPlaceholder} />
      <ContactsEditor contacts={contacts} onChange={setContacts} />

      {/* THE FOLLOW-UP. Same pairing as a task's deadline — pick "in a week", read back
          the date it means — and deliberately the same control, because "when do I come
          back to this" is one question however it is asked. What differs is everything
          around it: this date is not work, it carries no assignee and it is never
          completed, only moved. */}
      <DueOffsets value={form.follow_up_on} onPick={(d) => setForm((f) => ({ ...f, follow_up_on: d }))} />
      <DateInput label={he.leads.followUp} value={form.follow_up_on} onChange={set('follow_up_on')} />

      <Textarea
        label={he.leads.generalNotes}
        value={form.notes}
        onChange={set('notes')}
        placeholder={he.leads.generalNotesPlaceholder}
      />

      <ColorInput label={he.clients.color} value={form.color || '#3b82f6'} onChange={set('color')} />
      {error && <ErrorText error={error} />}
    </Modal>
  );
}
